import { useState, useCallback, useMemo } from 'react'
import { useConnectionStore } from '@/stores/connectionStore'
import { useAIStore } from '@/stores/aiStore'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertCircle,
  Compass,
  CheckSquare,
  Users,
  Loader2,
  Sparkles,
  FileText,
  Plus,
  ChevronRight,
  Info,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  Search,
  BarChart3,
  List,
  X,
  Download,
  MoreVertical,
  Pencil,
  Trash2,
  Check,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type {
  Interview,
  InterviewMode,
  QualityNote,
  Facet,
} from '@/types/interviewer'
import { sourceTypeToMode, getInterviewTypeConfig } from '@/types/interviewer'
import { FacetCard } from './FacetCard'
import { ScoreDisplay } from './ScoreDisplay'
import { GenericInterviewer } from './GenericInterviewer'
import { InsightsPanel } from './InsightsPanel'
import { useInterviewerStore } from '@/stores/interviewerStore'
import { searchFacets } from '@/lib/interviewer/clustering'
import { exportInterviewToPdf } from '@/lib/interviewer/exportPdf'

const MODE_CONFIG: Record<InterviewMode, {
  label: string
  icon: typeof Compass
  description: string
  color: string
}> = {
  discovery: {
    label: 'Discovery Interview',
    icon: Compass,
    description: 'Analyze customer conversations using Mom Test principles',
    color: 'var(--color-market)',
  },
  decisions: {
    label: 'Product Decision',
    icon: CheckSquare,
    description: 'Document the "why" behind decisions for future context',
    color: 'var(--color-solution)',
  },
  alignment: {
    label: 'Team Alignment',
    icon: Users,
    description: 'Identify misalignments and communication gaps',
    color: 'var(--color-business)',
  },
}

const MODE_BADGE_VARIANT: Record<InterviewMode, 'market' | 'business' | 'solution'> = {
  discovery: 'market',
  alignment: 'business',
  decisions: 'solution',
}

type ViewTab = 'list' | 'insights'

export function Interviewer() {
  const { isConnected } = useConnectionStore()
  const { isConfigured: aiConfigured } = useAIStore()
  const {
    sources,
    currentSource,
    isAnalyzing,
    setCurrentInterview,
    clearCurrentInterview,
    updateSource,
    deleteSource,
  } = useInterviewerStore()

  // Convert sources to interviews for backward compatibility
  const interviews = useMemo(() =>
    sources.map(s => ({ ...s, mode: sourceTypeToMode(s.sourceType) } as Interview)),
    [sources]
  )
  const currentInterview = currentSource
    ? { ...currentSource, mode: sourceTypeToMode(currentSource.sourceType) } as Interview
    : null

  const [selectedMode, setSelectedMode] = useState<InterviewMode | 'all'>('all')
  const [showGenericInterviewer, setShowGenericInterviewer] = useState(false)
  const [activeTab, setActiveTab] = useState<ViewTab>('list')
  const [searchQuery, setSearchQuery] = useState('')

  // Filter interviews by mode
  const filteredInterviews = useMemo(() => {
    let result = interviews
    if (selectedMode !== 'all') {
      result = result.filter(i => i.mode === selectedMode)
    }
    return result.sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
  }, [interviews, selectedMode])

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null
    return searchFacets(interviews, searchQuery)
  }, [interviews, searchQuery])

  const handleNewInterview = useCallback(() => {
    setShowGenericInterviewer(true)
  }, [])

  const handleInterviewerCancel = useCallback(() => {
    setShowGenericInterviewer(false)
  }, [])

  const handleInterviewerComplete = useCallback(() => {
    setShowGenericInterviewer(false)
  }, [])

  const handleSelectInterview = useCallback((interview: Interview) => {
    setCurrentInterview(interview)
  }, [setCurrentInterview])

  const handleRenameInterview = useCallback((id: string, newTitle: string) => {
    updateSource(id, { title: newTitle })
  }, [updateSource])

  const handleDeleteInterview = useCallback((id: string) => {
    deleteSource(id)
  }, [deleteSource])

  if (!isConnected) {
    return (
      <div className="h-full flex items-center justify-center">
        <Card className="max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 text-warning mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Not Connected</h3>
          <p className="text-text-muted text-sm">
            Connect to Fuseki in Settings to use the Interviewer.
          </p>
        </Card>
      </div>
    )
  }

  if (!aiConfigured) {
    return (
      <div className="h-full flex items-center justify-center">
        <Card className="max-w-md text-center p-8">
          <Sparkles className="w-12 h-12 text-solution mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">AI Not Configured</h3>
          <p className="text-text-muted text-sm">
            Configure your AI provider in Settings to analyze interviews.
          </p>
        </Card>
      </div>
    )
  }

  // Show Generic Interviewer workflow for new interview
  if (showGenericInterviewer) {
    return (
      <GenericInterviewer
        onCancel={handleInterviewerCancel}
        onComplete={handleInterviewerComplete}
      />
    )
  }

  // Show interview analysis view
  if (currentInterview) {
    return (
      <InterviewAnalysisView
        interview={currentInterview}
        onBack={clearCurrentInterview}
        isAnalyzing={isAnalyzing}
        onRename={handleRenameInterview}
        onDelete={handleDeleteInterview}
      />
    )
  }

  // Main view: tabs, filters, and content
  return (
    <div className="h-full flex flex-col gap-4">
      {/* Header with tabs and actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Tabs */}
          <div className="flex bg-warm-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('list')}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors',
                activeTab === 'list'
                  ? 'bg-background shadow-sm font-medium'
                  : 'text-text-muted hover:text-text'
              )}
            >
              <List className="w-4 h-4" />
              Interviews
            </button>
            <button
              onClick={() => setActiveTab('insights')}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors',
                activeTab === 'insights'
                  ? 'bg-background shadow-sm font-medium'
                  : 'text-text-muted hover:text-text'
              )}
            >
              <BarChart3 className="w-4 h-4" />
              Insights
            </button>
          </div>

          {/* Mode filter */}
          <select
            value={selectedMode}
            onChange={(e) => setSelectedMode(e.target.value as InterviewMode | 'all')}
            className="px-3 py-1.5 rounded-lg border border-border bg-bg text-sm"
          >
            <option value="all">All Modes</option>
            <option value="discovery">Discovery</option>
            <option value="decisions">Decisions</option>
            <option value="alignment">Alignment</option>
          </select>
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search facets..."
              className="pl-9 pr-8 py-1.5 w-64 rounded-lg border border-border bg-bg text-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <Button onClick={handleNewInterview} className="gap-2">
            <Plus className="w-4 h-4" />
            New Interview
          </Button>
        </div>
      </div>

      {/* Search Results */}
      {searchResults && searchResults.length > 0 && (
        <div className="border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium">
              Search Results ({searchResults.length})
            </h3>
            <button
              onClick={() => setSearchQuery('')}
              className="text-xs text-text-muted hover:text-text"
            >
              Clear
            </button>
          </div>
          <div className="space-y-2 max-h-64 overflow-auto">
            {searchResults.slice(0, 10).map(({ facet, interview }) => (
              <SearchResultCard
                key={facet.id}
                facet={facet}
                interview={interview}
                onClick={() => handleSelectInterview(interview)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'insights' ? (
          <InsightsPanel
            interviews={filteredInterviews}
            onSelectInterview={handleSelectInterview}
          />
        ) : filteredInterviews.length === 0 ? (
          <div className="py-16 text-center">
            <FileText className="w-10 h-10 text-text-muted mx-auto mb-4" />
            <h3 className="text-base font-medium mb-1">No interviews yet</h3>
            <p className="text-sm text-text-muted mb-4">
              {selectedMode === 'all'
                ? 'Paste a transcript to extract insights'
                : `Paste a transcript to extract ${selectedMode} insights`}
            </p>
            <Button onClick={handleNewInterview} variant="outline" className="gap-2">
              <Plus className="w-4 h-4" />
              Add Your First Interview
            </Button>
          </div>
        ) : (
          <div className="border-t border-border">
            {filteredInterviews.map((interview) => (
              <InterviewCard
                key={interview.id}
                interview={interview}
                onClick={() => handleSelectInterview(interview)}
                onRename={handleRenameInterview}
                onDelete={handleDeleteInterview}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// Search result card
function SearchResultCard({
  facet,
  interview,
  onClick,
}: {
  facet: Facet
  interview: Interview
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left p-2 rounded-lg bg-warm-50 hover:bg-warm-100 transition-colors"
    >
      <p className="text-sm line-clamp-1">{facet.content}</p>
      <div className="flex items-center gap-2 mt-1 text-xs text-text-muted">
        <span className="capitalize">{facet.type.replace(/_/g, ' ')}</span>
        <span>•</span>
        <span>{interview.title}</span>
      </div>
    </button>
  )
}

// Interview card for the list view
function InterviewCard({
  interview,
  onClick,
  onRename,
  onDelete,
}: {
  interview: Interview
  onClick: () => void
  onRename: (id: string, newTitle: string) => void
  onDelete: (id: string) => void
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(interview.title)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const facetCount = interview.facets.filter(f => !f.dismissed).length

  // Use interview type config if available, fall back to mode config
  const typeConfig = interview.interviewTypeId
    ? getInterviewTypeConfig(interview.interviewTypeId)
    : null
  const modeConfig = MODE_CONFIG[interview.mode]

  const label = typeConfig?.label ?? modeConfig.label
  const color = typeConfig?.color ?? modeConfig.color

  const handleSaveTitle = () => {
    if (editTitle.trim() && editTitle !== interview.title) {
      onRename(interview.id, editTitle.trim())
    }
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveTitle()
    } else if (e.key === 'Escape') {
      setEditTitle(interview.title)
      setIsEditing(false)
    }
  }

  const handleDelete = () => {
    onDelete(interview.id)
    setShowDeleteConfirm(false)
  }

  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-border hover:bg-bg-secondary transition-colors">
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onClick}>
          <div className="flex items-center gap-2 mb-1">
            {isEditing ? (
              <div className="flex items-center gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleSaveTitle}
                  autoFocus
                  className="flex-1 px-2 py-1 text-sm font-medium border border-border rounded focus:outline-none focus:ring-2 focus:ring-accent-500/30"
                />
                <button
                  onClick={handleSaveTitle}
                  className="p-1 hover:bg-bg-secondary rounded"
                >
                  <Check className="w-4 h-4 text-accent-500" />
                </button>
              </div>
            ) : (
              <h3 className="font-medium truncate">{interview.title}</h3>
            )}
            <Badge
              variant={MODE_BADGE_VARIANT[interview.mode]}
              className="text-xs flex-shrink-0"
            >
              {label}
            </Badge>
            {interview.linkedPersonName && (
              <Badge variant="secondary" className="text-xs flex-shrink-0">
                <Users className="w-3 h-3 mr-1" />
                {interview.linkedPersonName}
              </Badge>
            )}
          </div>
          {interview.interviewee && (
            <p className="text-sm text-text-muted mb-2">
              with {interview.interviewee}
            </p>
          )}
          <div className="flex items-center gap-4 text-xs text-text-muted">
            <span>{facetCount} facets</span>
            <span>
              {new Date(interview.createdAt).toLocaleDateString()}
            </span>
            {interview.mappingComplete && (
              <span className="flex items-center gap-1 text-success-fg">
                <CheckCircle className="w-3 h-3" />
                Mapped
              </span>
            )}
            {interview.status === 'analyzing' && (
              <span className="flex items-center gap-1 text-accent-600">
                <Loader2 className="w-3 h-3 animate-spin" />
                Analyzing...
              </span>
            )}
            {interview.status === 'error' && (
              <span className="flex items-center gap-1 text-danger-fg">
                <AlertCircle className="w-3 h-3" />
                Error
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {showDeleteConfirm ? (
            <div className="flex items-center gap-2 bg-danger-bg px-2 py-1 rounded" onClick={(e) => e.stopPropagation()}>
              <span className="text-xs text-danger-fg">Delete?</span>
              <button
                onClick={handleDelete}
                className="px-2 py-0.5 text-xs bg-danger-fg text-white rounded hover:opacity-90"
              >
                Yes
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-2 py-0.5 text-xs bg-bg-secondary rounded hover:bg-bg-tertiary"
              >
                No
              </button>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <button className="p-1.5 hover:bg-bg-secondary rounded transition-colors">
                  <MoreVertical className="w-4 h-4 text-text-muted" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => { setEditTitle(interview.title); setIsEditing(true) }}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-danger-fg focus:text-danger-fg"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <ChevronRight className="w-5 h-5 text-text-muted" />
        </div>
    </div>
  )
}

// Full interview analysis view
function InterviewAnalysisView({
  interview,
  onBack,
  isAnalyzing,
  onRename,
  onDelete,
}: {
  interview: Interview
  onBack: () => void
  isAnalyzing: boolean
  onRename: (id: string, newTitle: string) => void
  onDelete: (id: string) => void
}) {
  const [selectedFacetId, setSelectedFacetId] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(interview.title)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const config = MODE_CONFIG[interview.mode]

  const activeFacets = interview.facets.filter(f => !f.dismissed)
  const dismissedFacets = interview.facets.filter(f => f.dismissed)

  const handleSaveTitle = () => {
    if (editTitle.trim() && editTitle !== interview.title) {
      onRename(interview.id, editTitle.trim())
    }
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveTitle()
    } else if (e.key === 'Escape') {
      setEditTitle(interview.title)
      setIsEditing(false)
    }
  }

  const handleDelete = () => {
    onDelete(interview.id)
    onBack()
  }

  return (
    <div className="h-full flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ChevronRight className="w-4 h-4 rotate-180" />
            Back
          </Button>
          <div>
            {isEditing ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleSaveTitle}
                  autoFocus
                  className="px-2 py-1 text-lg font-semibold border border-border rounded focus:outline-none focus:ring-2 focus:ring-accent-500/30"
                />
                <button
                  onClick={handleSaveTitle}
                  className="p-1 hover:bg-bg-secondary rounded"
                >
                  <Check className="w-4 h-4 text-accent-500" />
                </button>
              </div>
            ) : (
              <h2 className="text-lg font-semibold">{interview.title}</h2>
            )}
            {interview.interviewee && (
              <p className="text-sm text-text-muted">with {interview.interviewee}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={MODE_BADGE_VARIANT[interview.mode]}>
            {config.label}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportInterviewToPdf(interview)}
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            Export PDF
          </Button>
          {showDeleteConfirm ? (
            <div className="flex items-center gap-2 bg-danger-bg px-3 py-1.5 rounded">
              <span className="text-sm text-danger-fg">Delete interview?</span>
              <button
                onClick={handleDelete}
                className="px-2 py-0.5 text-sm bg-danger-fg text-white rounded hover:opacity-90"
              >
                Yes
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-2 py-0.5 text-sm bg-bg-secondary rounded hover:bg-bg-tertiary"
              >
                No
              </button>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => { setEditTitle(interview.title); setIsEditing(true) }}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-danger-fg focus:text-danger-fg"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex gap-4 min-h-0 overflow-hidden">
        {/* Left panel: Transcript with highlights */}
        <div className="w-[46%] flex flex-col bg-bg-tertiary border-r border-border rounded-l-lg px-5 py-4 overflow-hidden">
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Transcript
          </h3>
          <div className="flex-1 overflow-auto text-sm whitespace-pre-wrap text-text-secondary leading-relaxed">
            {interview.rawContent}
          </div>
        </div>

        {/* Right panel: Facets and scores */}
        <div className="w-1/2 flex flex-col gap-4 overflow-hidden">
          {/* Scores */}
          <ScoreDisplay scores={interview.scores} sourceType={interview.sourceType} />

          {/* Quality Notes */}
          {interview.qualityNotes.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2">
                Quality Notes
              </h4>
              <div className="space-y-1.5">
                {interview.qualityNotes.map((note) => (
                  <QualityNoteItem key={note.id} note={note} />
                ))}
              </div>
            </div>
          )}

          {/* Facets */}
          <div className="flex-1 overflow-auto">
            <h4 className="text-sm font-medium mb-2 sticky top-0 bg-bg py-1">
              Extracted Facets ({activeFacets.length})
            </h4>

            {isAnalyzing ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-accent-500 mr-2" />
                <span className="text-text-muted">Analyzing interview...</span>
              </div>
            ) : activeFacets.length === 0 ? (
              <div className="p-4 text-center text-text-muted text-sm">
                No facets extracted yet
              </div>
            ) : (
              <div className="space-y-2">
                {activeFacets.map((facet) => (
                  <FacetCard
                    key={facet.id}
                    facet={facet}
                    isSelected={selectedFacetId === facet.id}
                    onClick={() => setSelectedFacetId(
                      selectedFacetId === facet.id ? null : facet.id
                    )}
                  />
                ))}
              </div>
            )}

            {dismissedFacets.length > 0 && (
              <details className="mt-4">
                <summary className="text-sm text-text-muted cursor-pointer">
                  {dismissedFacets.length} dismissed facets
                </summary>
                <div className="space-y-2 mt-2 opacity-50">
                  {dismissedFacets.map((facet) => (
                    <FacetCard
                      key={facet.id}
                      facet={facet}
                      isSelected={false}
                      onClick={() => {}}
                    />
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function QualityNoteItem({ note }: { note: QualityNote }) {
  const icons = {
    warning: AlertTriangle,
    suggestion: Lightbulb,
    success: CheckCircle,
    error: AlertCircle,
  }
  const stripClasses = {
    warning: 'bg-warning-bg text-warning-fg',
    suggestion: 'bg-solution-50 text-solution-700',
    success: 'bg-success-bg text-success-fg',
    error: 'bg-danger-bg text-danger-fg',
  }
  const Icon = icons[note.type]

  return (
    <div className={cn('flex items-start gap-2 rounded px-3 py-2 text-xs', stripClasses[note.type])}>
      <Icon className="w-3 h-3 mt-0.5 flex-shrink-0" />
      <span>{note.message}</span>
    </div>
  )
}
